// public-contact — the CEO's "Umów spotkanie" dialog on the public pages, wired to POST /api/contact-messages/.
// One controller on <body>: any [data-action="public-contact#open"] opens the <dialog> (optional
// data-public-contact-category-param preselects a branża); the form sends a ContactMessage (subject from
// data-public-contact-subject-value, body composed from the branża, RODO consent) with the page's CSRF token.
import { Controller } from '@hotwired/stimulus';

const SUCCESS = 'Dziękujemy — odezwiemy się wkrótce.';
const FAILURE = 'Nie udało się wysłać zgłoszenia. Spróbuj ponownie.';
const BUSY = 'Wysyłanie…';
const NO_CATEGORY = 'nie wybrano';
const FIELD_ERRORS = { full_name: 'nameError', email: 'emailError', gdpr_consent: 'consentError' };

export default class extends Controller {
  static targets = [
    'dialog', 'form', 'name', 'nameError', 'email', 'emailError', 'category', 'choice',
    'consent', 'consentError', 'submit', 'notice',
  ];

  static values = {
    url: { type: String, default: '/api/contact-messages/' },
    subject: { type: String, default: 'cooperation' },
  };

  open(event) {
    this.opener = event?.currentTarget ?? null;
    const category = event?.params?.category;
    if (category) this.#select(category);
    this.dialogTarget.showModal();
    this.nameTarget.focus();
  }

  close() {
    this.dialogTarget.close();
  }

  // A click whose target is the <dialog> itself landed on the backdrop (content clicks hit its children).
  backdrop(event) {
    if (event.target === this.dialogTarget) this.close();
  }

  restore() {
    this.opener?.focus();
    this.opener = null;
  }

  pick(event) {
    const category = event.params.category;
    this.#select(this.categoryTarget.value === category ? '' : category);
  }

  async submit(event) {
    event.preventDefault();
    if (this.sending) return;
    this.#clearErrors();
    if (!this.formTarget.reportValidity()) return;

    this.#busy(true);
    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRFToken': this.#csrf() },
        body: JSON.stringify(this.#payload()),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) this.#succeed();
      else this.#fail(response.status, data);
    } catch {
      this.#notice(FAILURE, true);
    } finally {
      this.#busy(false);
    }
  }

  #payload() {
    return {
      full_name: this.nameTarget.value.trim(),
      email: this.emailTarget.value.trim(),
      subject: this.subjectValue,
      body: `Prośba o spotkanie. Branża: ${this.categoryTarget.value || NO_CATEGORY}`,
      gdpr_consent: this.consentTarget.checked,
    };
  }

  #succeed() {
    this.formTarget.reset();
    this.#select('');
    this.#notice(SUCCESS, false);
  }

  #fail(status, data) {
    const detail = data?.error?.detail ?? data?.detail;
    if (status === 400 && detail && typeof detail === 'object') {
      let mapped = false;
      for (const [field, targetName] of Object.entries(FIELD_ERRORS)) {
        if (!detail[field]) continue;
        const messages = Array.isArray(detail[field]) ? detail[field] : [detail[field]];
        this.#fieldError(targetName, messages.join(' '));
        mapped = true;
      }
      if (!mapped) this.#notice(FAILURE, true);
      return;
    }
    this.#notice(typeof detail === 'string' && detail ? detail : FAILURE, true);
  }

  #select(category) {
    this.categoryTarget.value = category;
    for (const button of this.choiceTargets) {
      const pressed = Boolean(category) && button.dataset.publicContactCategoryParam === category;
      button.setAttribute('aria-pressed', String(pressed));
    }
  }

  #busy(on) {
    this.sending = on;
    this.submitTarget.disabled = on;
    if (on) {
      this.idleLabel = this.submitTarget.textContent;
      this.submitTarget.textContent = BUSY;
    } else if (this.idleLabel) {
      this.submitTarget.textContent = this.idleLabel;
    }
  }

  #notice(text, isError) {
    this.noticeTarget.textContent = text;
    this.noticeTarget.classList.toggle('is-error', isError);
    this.noticeTarget.hidden = false;
  }

  #fieldError(targetName, text) {
    const el = this[`${targetName}Target`];
    el.textContent = text;
    el.hidden = false;
  }

  #clearErrors() {
    for (const targetName of Object.values(FIELD_ERRORS)) {
      const el = this[`${targetName}Target`];
      el.textContent = '';
      el.hidden = true;
    }
    this.noticeTarget.hidden = true;
  }

  #csrf() {
    return this.formTarget.querySelector('[name="csrfmiddlewaretoken"]')?.value ?? '';
  }
}
